import { useEffect, useState } from 'react';
import { useCalendlyEventListener, PopupModal } from 'react-calendly';
import PropTypes from 'prop-types';
import { IconAlertCircleFilled } from '@tabler/icons-react';

import appSettings from '../../app-settings';
import { useAccount } from '../../store/account/hooks';
import { useDomains } from '../../store/domains/hooks';
import Button from '../buttons/Button';
import AlertMessage from '../alerts/AlertMessage';
import { ONBOARDING_STEPS } from '../../core/constants';
import Loader from '../loader/Loader';
import StepNavBottom from './StepNavBottom';

const ScheduleCallStep = ({ onPrev, setStep }) => {
  const [open, setOpen] = useState(false);
  const { account, loading, error, setError, setOnboardingCall } = useAccount();
  const { authenticatedDomain, getDomains, loading: domainsLoading } = useDomains();

  const hasValidDomain = !!authenticatedDomain;

  useEffect(() => {
    // Removes query string params from previous step (if any)
    window.history.replaceState({}, document.title, '/');
    getDomains();
  }, [getDomains]);

  useCalendlyEventListener({
    onEventScheduled: () => setOnboardingCall(),
  });

  const handleModalClose = () => {
    setOpen(false);
    if (account?.onboardingCall) {
      setStep(ONBOARDING_STEPS.congrats);
    }
  };

  return (
    <div className="m-auto max-w-2xl space-y-12 pb-[60px] 2xl:max-w-5xl">
      <div className="space-y-4">
        <h1 className="text-h1 font-semibold">Schedule Your Onboarding Call</h1>
        <div className="space-y-6 text-lg">
          <p>
            You have successfully completed the initial setup of your account. You can now connect with new donors,
            create and send campaigns, and process donations.
          </p>
          <p>
            As a final step, please schedule your 1:1 onboarding call with us; this is where we&apos;ll walk you through
            the DonorSpring platform and share essential tips and best practices.
          </p>
        </div>
      </div>
      {!loading && !domainsLoading && (
        <div className="flex flex-col items-center space-y-4">
          {hasValidDomain ? (
            <>
              <Button
                title="Schedule Onboarding Call"
                color="primary"
                size="xl"
                className="w-full max-w-md"
                onClick={() => setOpen(true)}
                disabled={loading}
              />
              <Button
                title="I Already Scheduled My Call"
                color="destructive"
                variant="outlined"
                size="lg"
                className="w-full max-w-md"
                onClick={async () => {
                  await setOnboardingCall();
                  setStep(ONBOARDING_STEPS.congrats);
                }}
                disabled={loading}
              />
            </>
          ) : (
            <>
              <div className="flex items-center gap-3 rounded-md bg-orange-50 px-3 py-2">
                <IconAlertCircleFilled size={20} className="inline-block shrink-0 text-orange-500" />
                <p className="text-base">
                  You must complete domain authentication before scheduling your onboarding call.
                </p>
              </div>
              <Button
                title="Go to Domain Authentication"
                color="primary"
                size="xl"
                className="w-full max-w-sm"
                onClick={() => setStep(ONBOARDING_STEPS.domainAuthentication)}
                disabled={loading}
              />
            </>
          )}
        </div>
      )}
      <PopupModal
        url={appSettings.calendlyUrl}
        onModalClose={handleModalClose}
        open={open}
        rootElement={document.getElementById('root')}
        prefill={{ name: account?.organizationName }}
      />

      {/* calendly popup modal z-index is 9999 */}
      {(loading || domainsLoading) && <Loader className="z-[99999]" />}

      <StepNavBottom onPrev={onPrev} nextDisabled={true} />

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

ScheduleCallStep.propTypes = {
  onPrev: PropTypes.func.isRequired,
  setStep: PropTypes.func.isRequired,
};

export default ScheduleCallStep;
